import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

const Shell = ({ children, testid }) => (
  <div className="mx-auto max-w-xl px-5 py-24 text-center" data-testid={testid}>
    {children}
  </div>
);

export function JoinRoom() {
  const { code } = useParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState({ status: "working" });

  useEffect(() => {
    if (loading) return;
    if (!user) {
      localStorage.setItem("cml_pending_join", code);
      nav(`/signup?join=${code}`, { replace: true });
      return;
    }
    api
      .post("/rooms/join", { invite_code: code })
      .then(({ data }) => {
        localStorage.removeItem("cml_pending_join");
        setState({ status: "joined", name: data.name });
        setTimeout(() => nav("/messages", { replace: true }), 1200);
      })
      .catch((e) => setState({ status: "error", message: apiError(e.response?.data?.detail) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  return (
    <Shell testid="join-room-page">
      {state.status === "working" && <Loader2 className="mx-auto h-8 w-8 animate-spin text-brand" />}
      {state.status === "joined" && (
        <>
          <CheckCircle2 className="mx-auto h-10 w-10 text-forest" />
          <h1 className="mt-5 font-display text-3xl font-extrabold tracking-tighter">
            You're in {state.name}
          </h1>
          <p className="mt-2 text-sm text-ink-soft">Taking you to the conversation…</p>
        </>
      )}
      {state.status === "error" && (
        <>
          <XCircle className="mx-auto h-10 w-10 text-destructive" />
          <h1 className="mt-5 font-display text-3xl font-extrabold tracking-tighter">Invite not valid</h1>
          <p className="mt-2 text-sm text-ink-soft">{state.message}</p>
          <Link to="/messages">
            <Button className="mt-8 min-h-[48px] rounded-full bg-brand px-7 hover:bg-brand-hover">
              Go to TransMessages
            </Button>
          </Link>
        </>
      )}
    </Shell>
  );
}

export function JoinTeam() {
  const { token } = useParams();
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("idle");

  useEffect(() => {
    api
      .get(`/teams/invites/${token}`)
      .then(({ data }) => setInfo(data))
      .catch(() => setState("invalid"));
  }, [token]);

  const accept = async () => {
    if (!user) return nav(`/signup?team=${token}`);
    setState("working");
    try {
      await api.post(`/teams/invites/${token}/accept`);
      await refresh();
      setState("done");
      setTimeout(() => nav("/settings", { replace: true }), 1200);
    } catch {
      setState("invalid");
    }
  };

  return (
    <Shell testid="join-team-page">
      {state === "invalid" ? (
        <>
          <XCircle className="mx-auto h-10 w-10 text-destructive" />
          <h1 className="mt-5 font-display text-3xl font-extrabold tracking-tighter">
            Invite not valid
          </h1>
          <p className="mt-2 text-sm text-ink-soft">This team invite was already used or removed.</p>
        </>
      ) : state === "done" ? (
        <>
          <CheckCircle2 className="mx-auto h-10 w-10 text-forest" />
          <h1 className="mt-5 font-display text-3xl font-extrabold tracking-tighter">Seat claimed</h1>
          <p className="mt-2 text-sm text-ink-soft">Premium features are on for your account.</p>
        </>
      ) : (
        <>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter">
            {info?.team_name || "A ChatMiLu team"} invited you
          </h1>
          <p className="mt-2 text-sm text-ink-soft">
            Claim your seat to unlock Premium AI, translated rooms and calls{info?.email ? ` for ${info.email}` : ""}.
          </p>
          <Button
            data-testid="accept-team-invite"
            onClick={accept}
            disabled={state === "working" || loading}
            className="mt-8 min-h-[48px] rounded-full bg-brand px-7 hover:bg-brand-hover active:scale-95"
          >
            {state === "working" && <Loader2 className="me-2 h-4 w-4 animate-spin" />} Claim my seat
          </Button>
        </>
      )}
    </Shell>
  );
}
